import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './ManualEntryInputNumber.module.css'

interface ManualEntryInputNumberProps {
  maxDigits: number
  onComplete: (number: string) => void
}

const ManualEntryInputNumber: React.FC<ManualEntryInputNumberProps> = ({
  maxDigits,
  onComplete,
}) => {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState<string[]>(
    Array(maxDigits).fill(''),
  )
  const [activeIndex, setActiveIndex] = useState(0)

  // Reset input if maxDigits changes
  React.useEffect(() => {
    setInputValue(Array(maxDigits).fill(''))
    setActiveIndex(0)
  }, [maxDigits])

  const handleNumberPress = (num: string) => {
    if (activeIndex < maxDigits) {
      const newValue = [...inputValue]
      newValue[activeIndex] = num
      setInputValue(newValue)
      if (activeIndex === maxDigits - 1) {
        const number = newValue.join('')
        onComplete(number)

        // Give some delay to show the number before reset
        setTimeout(() => {
          setInputValue(Array(maxDigits).fill(''))
          setActiveIndex(0)
        }, 500)
      } else {
        setActiveIndex((prev) => Math.min(prev + 1, maxDigits - 1))
      }
    }
  }

  const handleBackspace = () => {
    if (activeIndex > 0 || inputValue[0] !== '') {
      const newValue = [...inputValue]
      if (inputValue[activeIndex] !== '') {
        newValue[activeIndex] = ''
      } else {
        newValue[Math.max(0, activeIndex - 1)] = ''
        setActiveIndex((prev) => Math.max(0, prev - 1))
      }
      setInputValue(newValue)
    }
  }

  const handleClear = () => {
    // Clear all input numbers
    setInputValue(Array(maxDigits).fill(''))
    setActiveIndex(0)
  }

  return (
    <div className="flex flex-col items-center">
      <h5 className="text-[var(--caption-text)] text-center text-base font-medium mb-2">
        {t('lottoPage.inputNumber')}
      </h5>
      {/* Number Display */}
      <div className="flex gap-2 sm:gap-3 justify-center mb-3 sm:mb-4">
        {inputValue.map((value, index) => (
          <div
            key={index}
            className={`w-[45px] h-[45px] sm:w-[60px] sm:h-[60px] my-2 sm:my-4 flex items-center justify-center text-xl sm:text-2xl font-semibold rounded-lg transition-all ${
              index === activeIndex
                ? 'bg-[var(--bet-type-hover-bg)] text-[var(--bet-type-hover-text)] border-2 border-[var(--bet-type-active-border)]'
                : 'bg-[var(--bet-type-bg)] text-[var(--bet-type-text)] border-2 border-[var(--bet-type-border)]'
            }`}
          >
            {value}
          </div>
        ))}
      </div>
      {/* Number Keypad */}
      <div
        className={`grid grid-cols-3 gap-2 sm:gap-3 w-full ${styles.keypadGrid}`}
      >
        {[...Array(9)].map((_, i) => (
          <button
            key={i + 1}
            className="keypad-btn border rounded-lg bg-[var(--grid-item-bg)] text-[var(--grid-item-text)] border-[var(--grid-item-border)] text-xl sm:text-2xl font-semibold cursor-pointer transition-all hover:bg-[var(--grid-item-hover-bg)] hover:text-[var(--grid-item-hover-text)] hover:border-[var(--grid-item-hover-border)]"
            onClick={() => handleNumberPress((i + 1).toString())}
          >
            {i + 1}
          </button>
        ))}
        <button
          className="keypad-btn border rounded-lg bg-[var(--grid-item-bg)] text-[var(--grid-item-text)] border-[var(--grid-item-border)] text-xl sm:text-2xl font-semibold cursor-pointer transition-all hover:bg-[var(--grid-item-hover-bg)] hover:text-[var(--grid-item-hover-text)] hover:border-[var(--grid-item-hover-border)]"
          onClick={handleBackspace}
        >
          ←
        </button>
        <button
          className="keypad-btn border rounded-lg bg-[var(--grid-item-bg)] text-[var(--grid-item-text)] border-[var(--grid-item-border)] text-xl sm:text-2xl font-semibold cursor-pointer transition-all hover:bg-[var(--grid-item-hover-bg)] hover:text-[var(--grid-item-hover-text)] hover:border-[var(--grid-item-hover-border)]"
          onClick={() => handleNumberPress('0')}
        >
          0
        </button>
        <button
          className="keypad-btn border rounded-lg bg-[var(--grid-item-bg)] text-[var(--grid-item-text)] border-[var(--grid-item-border)] text-xl sm:text-2xl font-semibold cursor-pointer transition-all hover:bg-[var(--grid-item-hover-bg)] hover:text-[var(--grid-item-hover-text)] hover:border-[var(--grid-item-hover-border)] flex items-center justify-center"
          onClick={handleClear}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-4 h-4 sm:w-5 sm:h-5"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>
  )
}

export default ManualEntryInputNumber
